import React from "react";
import { useTranslation } from "react-i18next";
import { Box, TableCell, TableSortLabel } from "@mui/material";
import { makeStyles } from "@mui/styles";
import RadioIcon from "@iconify/icons-mdi/checkbox-marked-circle-outline";
import MagnifyIcon from "@iconify/icons-mdi/magnify";
import { InlineIcon } from "@iconify/react";
import {
  OcDataTableFilterForm,
  OcDataTableFilterIcon,
  OcDataTableHeaderSearchForm,
} from "oc/components";

const useStyles = makeStyles({
  customTableCell: {
    "&.MuiTableCell-head:first-of-type": {
      borderTopLeftRadius: "0px",
      borderBottomLeftRadius: "0px",
    },
    "&.MuiTableCell-head:last-of-type": {
      borderTopRightRadius: "0px",
      borderBottomRightRadius: "0px",
    },
  },
});

export interface OcDateTableData {
  // sorting / search
  sortField?: string;
  sortDirection?: "asc" | "desc";
  searchField?: string;
  searchText?: string;
  showSearchForm?: boolean;
  page: number;

  // 👇 filter fields are now non‐optional
  filterField: string;
  filterSelected: string[];
  filterValues: string[];
  filterShowForm: boolean;
}

type OnSearchChangeType = ({
  searchText,
  searchField,
}: {
  searchField: string;
  searchText: string;
}) => void;

interface OcDataTableHeaderCellProps {
  tableData: OcDateTableData;
  setTableData: (data: Partial<OcDateTableData>) => void;
  onSearchChange?: OnSearchChangeType;
  column: {
    align?: "left" | "right" | "inherit" | "center" | "justify";
    anchor?: any;
    filter?: boolean;
    id: string;
    label: string;
    minWidth?: string;
    search?: boolean;
    sort?: boolean;
    type?: string;
    width?: string;
    zeroPadding?: boolean;
  };
  dailyTable?: boolean;
  tableId?: string;
}

export const OcDataTableHeaderCell: React.FC<OcDataTableHeaderCellProps> = ({
  tableData,
  setTableData,
  onSearchChange,
  column,
  tableId,
}) => {
  const classes = useStyles();
  const {
    align = "left",
    filter,
    id,
    label,
    minWidth,
    search,
    sort,
    type,
    width,
    zeroPadding,
  } = column;
  const { t } = useTranslation();

  const positionValue: React.CSSProperties["position"] =
    tableId === "employeeAdmissionsApprovelTableData"
      ? "sticky"
      : "relative";

  let cellStyle = {
    position: positionValue,
    height: "35px",
    top: 0,
    zIndex: 1,
    ...(zeroPadding && { paddingTop: 0, paddingBottom: 0 }),
    ...(width && {
      width,
      maxWidth: width,
      minWidth: minWidth || width,
    }),
  };

  if (zeroPadding) {
    cellStyle = Object.assign({}, cellStyle, {
      paddingTop: "0px",
      paddingBottom: "0px",
    });
  }

  if (width) {
    cellStyle = Object.assign({}, cellStyle, {
      width: width,
      maxWidth: width,
      minWidth: width,
    });
  }

  let localizedLabel = t(label);
  let capitalizedLabel =
    localizedLabel.charAt(0).toUpperCase() + localizedLabel.slice(1);

  const onSortChange = (field: string) => {
    if (field !== tableData?.sortField) {
      setTableData({
        sortField: field,
        sortDirection: "asc",
      });
      return;
    }
    let newDirection: "asc" | "desc" =
      tableData?.sortDirection === "asc" ? "desc" : "asc";
    if (newDirection !== tableData?.sortDirection) {
      setTableData({ sortDirection: newDirection });
    }
  };

  if (type === "radio")
    return (
      <TableCell
        align={align || "left"}
        className={tableId === "MyTimeTable" ? classes.customTableCell : ""}
        style={
          width
            ? {
              textAlign: align,
              width: width,
              maxWidth: width,
              minWidth: minWidth ? minWidth : width,
              position: "relative",
              overflow: "visible",
            }
            : { textAlign: align, position: "relative", overflow: "visible" }
        }
      >
        <Box
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <InlineIcon
            icon={RadioIcon}
            style={{
              width: "20px",
              height: "20px",
            }}
          />
        </Box>
      </TableCell>
    );

  if (type === "avatar")
    return (
      <TableCell
        className={tableId === "MyTimeTable" ? classes.customTableCell : ""}
        style={
          width
            ? {
              textAlign: align,
              width: width,
              maxWidth: width,
              minWidth: minWidth ? minWidth : width,
              position: "relative",
              overflow: "visible",
            }
            : { textAlign: align, position: "relative", overflow: "visible" }
        }
      >
        <Box
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          marginLeft="-4px"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
          >
            <rect width="36" height="36" rx="18" fill="#F2F4F8" />
            <path
              d="M14.534 18.07C14.6569 18.0175 14.7892 17.9903 14.9228 17.9898C15.0565 17.9893 15.1889 18.0156 15.3122 18.0672C15.4355 18.1188 15.5473 18.1945 15.6408 18.29C15.7344 18.3855 15.8078 18.4988 15.8568 18.6231C15.9058 18.7475 15.9294 18.8804 15.9262 19.014C15.9229 19.1477 15.8929 19.2793 15.8379 19.4011C15.7829 19.523 15.7041 19.6325 15.606 19.7234C15.508 19.8142 15.3927 19.8845 15.267 19.93C14.5985 20.1934 14.0248 20.6519 13.6205 21.2458C13.2162 21.8397 13 22.5415 13 23.26V25C13 25.2652 13.1054 25.5196 13.2929 25.7071C13.4804 25.8946 13.7348 26 14 26H22C22.2652 26 22.5196 25.8946 22.7071 25.7071C22.8946 25.5196 23 25.2652 23 25V23.353C23.0001 22.6115 22.7749 21.8874 22.3541 21.2768C21.9334 20.6662 21.337 20.1979 20.644 19.934C20.5175 19.8901 20.4012 19.8213 20.3018 19.7316C20.2024 19.6419 20.1221 19.5332 20.0655 19.4119C20.009 19.2905 19.9773 19.1591 19.9725 19.0253C19.9677 18.8916 19.9897 18.7582 20.0374 18.6331C20.0851 18.508 20.1574 18.3938 20.25 18.2972C20.3427 18.2006 20.4538 18.1235 20.5767 18.0706C20.6997 18.0177 20.832 17.9901 20.9659 17.9893C21.0998 17.9885 21.2324 18.0146 21.356 18.066C22.4276 18.4742 23.3499 19.1983 24.0006 20.1425C24.6514 21.0867 24.9999 22.2063 25 23.353V25C25 25.7956 24.6839 26.5587 24.1213 27.1213C23.5587 27.6839 22.7956 28 22 28H14C13.2044 28 12.4413 27.6839 11.8787 27.1213C11.3161 26.5587 11 25.7956 11 25V23.26C11.0001 22.1402 11.3373 21.0463 11.9676 20.1206C12.5978 19.195 13.4921 18.4805 14.534 18.07ZM18 8C19.0609 8 20.0783 8.42143 20.8284 9.17157C21.5786 9.92172 22 10.9391 22 12V14C22 15.0609 21.5786 16.0783 20.8284 16.8284C20.0783 17.5786 19.0609 18 18 18C16.9391 18 15.9217 17.5786 15.1716 16.8284C14.4214 16.0783 14 15.0609 14 14V12C14 10.9391 14.4214 9.92172 15.1716 9.17157C15.9217 8.42143 16.9391 8 18 8V8ZM18 10C17.4696 10 16.9609 10.2107 16.5858 10.5858C16.2107 10.9609 16 11.4696 16 12V14C16 14.5304 16.2107 15.0391 16.5858 15.4142C16.9609 15.7893 17.4696 16 18 16C18.5304 16 19.0391 15.7893 19.4142 15.4142C19.7893 15.0391 20 14.5304 20 14V12C20 11.4696 19.7893 10.9609 19.4142 10.5858C19.0391 10.2107 18.5304 10 18 10Z"
              fill="#919699"
            />
          </svg>
        </Box>
      </TableCell>
    );

  return (
    <TableCell
      align={align || "left"}
      style={cellStyle}
      className={tableId === "MyTimeTable" ? classes.customTableCell : ""}
    >
      {sort ? (
        <TableSortLabel
          active={tableData?.sortField === id ? true : false}
          direction={tableData?.sortDirection}
          onClick={() => onSortChange(id)}
        >
          {capitalizedLabel}
        </TableSortLabel>
      ) : (
        capitalizedLabel
      )}
      {search && (
        <InlineIcon
          icon={MagnifyIcon}
          onClick={() => onSearchChange!({ searchField: id, searchText: "" })}
          style={{
            float: "right",
            width: "20px",
            height: "20px",
            cursor: "pointer",
            marginRight: "16px",
            marginLeft: "16px",
            marginTop: "4px",
          }}
        />
      )}
      {filter && (
        <OcDataTableFilterIcon
          align={align as "left" | "right"}
          id={id}
          tableData={tableData}
          setTableData={setTableData}
        />
      )}
      <OcDataTableHeaderSearchForm
        id={id}
        searchText={tableData?.searchText || ""}
        onSearchChange={onSearchChange!}
        searchField={tableData?.searchField || ""}
      />
      {tableData?.filterShowForm && tableData?.filterField === id && (
        <OcDataTableFilterForm
          tableData={tableData}
          setTableData={setTableData}
        />
      )}
    </TableCell>
  );
};

export default OcDataTableHeaderCell;
